import { useEffect, useState } from "react";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import { fmtMoney, fmtNum } from "@/lib/format";
import { toast } from "sonner";

export default function Intercompany() {
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    const [filter, setFilter] = useState("all");
    const load = () => client.get("/intercompany")
        .then((r) => setData(r.data))
        .catch((e) => { if (e.response?.status === 403) setDenied(true); else toast.error(e.response?.data?.detail || "Load failed"); });
    useEffect(() => { load(); }, []);
    if (denied) return <AccessDenied />;
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;

    const eliminate = async () => {
        try {
            const r = await client.post("/intercompany/eliminate", { only_matched: true });
            toast.success(`Eliminated ${r.data.eliminated_count} pairs · ${fmtMoney(r.data.total_eliminated_musd)}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const rows = data.transactions.filter((t) => filter === "all" || t.status === filter).slice(0, 100);

    return (
        <div>
            <PageHeader overline={`${data.stats.total} transactions`} title="Intercompany Matrix" description="Reconciliation of intra-group receivables and payables. Mismatches must clear before consolidation.">
                <button data-testid="run-eliminate" onClick={eliminate} className="bg-fp-ink text-white px-5 py-2 text-sm">Run Elimination</button>
            </PageHeader>
            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-8">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <KpiCard label="Total IC Value" value={fmtMoney(data.stats.total_receivable_musd, { decimals: 0 })} />
                    <KpiCard label="Matched" value={fmtNum(data.stats.matched)} subValue={`${Math.round(data.stats.matched / data.stats.total * 100)}%`} />
                    <KpiCard label="Unmatched" value={fmtNum(data.stats.unmatched)} testId="ic-unmatched" />
                    <KpiCard label="Total Mismatch" value={fmtMoney(data.stats.total_mismatch_musd)} />
                    <KpiCard label="Elimination Impact" value={fmtMoney(data.stats.elimination_impact_musd, { decimals: 0 })} />
                </div>
                <div className="flex gap-2">
                    {["all", "matched", "unmatched", "eliminated"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-sans ${filter === f ? "bg-fp-ink text-white" : "border border-fp-border text-fp-secondary hover:border-fp-ink"}`}>{f}</button>
                    ))}
                </div>
                <div className="bg-white border border-fp-border p-6 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-fp-ink">
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">From</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">To</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Type</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">From Reports</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">To Reports</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Mismatch</th>
                                <th className="pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((t) => (
                                <tr key={t.id} className="border-b border-fp-border hover:bg-fp-bg">
                                    <td className="py-2.5 font-sans text-xs text-fp-ink">{t.from_entity_name}</td>
                                    <td className="py-2.5 font-sans text-xs text-fp-ink">{t.to_entity_name}</td>
                                    <td className="py-2.5 font-sans text-xs text-fp-secondary">{t.type}</td>
                                    <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(t.reported_by_from_musd)}</td>
                                    <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(t.reported_by_to_musd)}</td>
                                    <td className={`py-2.5 text-right font-mono text-sm ${Math.abs(t.mismatch_musd) > 0.1 ? "text-fp-terracotta" : "text-fp-success"}`}>{fmtMoney(t.mismatch_musd)}</td>
                                    <td className="py-2.5 font-mono text-[10px] uppercase text-fp-secondary">{t.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
